import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
// @mui
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
// hooks
import useAuth from 'hooks/useAuth';
import useResponsive from 'hooks/useResponsive';
import useReferrals from 'hooks/useReferrals';
// constants
import ROUTES from 'constants/routes';
import WP_ROUTES from 'constants/wordpress-routes';
// components
import Seo from 'components/Seo';
import Link from 'components/Link';
import Page from 'components/Page';
import Logo from 'components/Logo';
import LoginForm from 'components/LoginForm';
// assets
import LoginIllustration from 'assets/illustration-login';
import NoIndex from 'components/Seo/NoIndex';
import { useLocalStorage } from 'react-use';

const StyledRoot = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const StyledHeader = styled('header')(({ theme }) => ({
  top: 0,
  zIndex: 9,
  lineHeight: 0,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  alignItems: 'center',
  padding: theme.spacing(3),
  justifyContent: 'space-between',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
  },
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(7, 5, 0, 7),
  },
}));

const StyledSection = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 460,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',
  justifyContent: 'center',
  borderRadius: theme.spacing(1),
  margin: theme.spacing(1, 0, 2, 2),
}));

const StyledContent = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
}));

const LoginPage = () => {
  const router = useRouter();
  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');

  const { isAuthenticated, user } = useAuth();
  const { queryParamsString } = useReferrals();

  const registerUrl = `${WP_ROUTES.register}?${queryParamsString}`;

  const [redirectUrl, setRedirectUrl] = useLocalStorage('redirect-url', '');

  const [redirectTo, setRedirectTo] = useState('/');

  /** SEO values */
  const seoTitle = 'Join Us | CREOATE';
  const seoDescription =
    'Discover unique wholesale products and buy with just a few clicks. Shop from independent brands with stress-free shopping anytime, day or night.';

  useEffect(() => {
    const handleRedirect = async () => {
      const defaultRoute = user?.userType !== 'Retailer' ? '/' : '/for-you';

      if (router.asPath) {
        const splittedTextRoute = router.asPath.split('redirectTo=')[1];
        if (
          ['/', '/my-account/customer-logout', '/auth/login'].includes(splittedTextRoute) &&
          user?.userType === 'Retailer'
        ) {
          setRedirectTo(defaultRoute);
        } else if (splittedTextRoute) {
          setRedirectTo(splittedTextRoute);
        } else if (redirectUrl && redirectUrl !== '') {
          setRedirectTo(redirectUrl);
        }
      }

      if (isAuthenticated && user?.shippingCountry) {
        const route = redirectTo || redirectUrl || defaultRoute;
        await router.push(route);
        setRedirectUrl(null);
      }
    };

    handleRedirect();
  }, [isAuthenticated]);

  const handleLogoClick = () => router.push(ROUTES.page.root);

  return (
    !isAuthenticated && (
      <Page>
        <Seo title={seoTitle} description={seoDescription} />
        <NoIndex />
        <StyledRoot>
          <StyledHeader>
            <Logo variant="full" onClick={handleLogoClick} />
            {smUp && (
              <Typography variant="body2" sx={{ mt: { md: -2 } }}>
                Don’t have an account?{' '}
                <Link variant="subtitle2" href={registerUrl}>
                  Get started
                </Link>
              </Typography>
            )}
          </StyledHeader>

          {mdUp && (
            <StyledSection elevation={6}>
              <Typography variant="h4" sx={{ px: 5, mt: 3, my: 5 }}>
                Hi, Welcome Back
              </Typography>
              <LoginIllustration />
            </StyledSection>
          )}

          <Container maxWidth="sm">
            <StyledContent>
              <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="h4" gutterBottom>
                    Login to your Account
                  </Typography>
                  <Typography sx={{ color: 'text.secondary' }}>Enter your details below.</Typography>
                </Box>
              </Stack>

              <LoginForm redirectTo={redirectTo} />

              {!smUp && (
                <Typography variant="body2" align="center" sx={{ mt: 3 }}>
                  Don&apos;t have an account?{' '}
                  <Link variant="subtitle2" href={registerUrl}>
                    Get started
                  </Link>
                </Typography>
              )}
            </StyledContent>
          </Container>
        </StyledRoot>
      </Page>
    )
  );
};

LoginPage.getLayout = ({ children }) => children;

export default LoginPage;
